import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Globe } from "lucide-react";
import { API } from "../../lib/adventureApi";
import { authHeader } from "../../lib/auth";
import { MARKETS } from "../../lib/market";

const EDITABLE = ["uk", "eu", "au"];

export default function CurrencyAdmin() {
  const [rates, setRates] = useState({});
  const [round, setRound] = useState(50);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = () => {
    setLoading(true);
    fetch(`${API}/fx-rates`)
      .then((r) => r.json())
      .then((d) => {
        setRates(d.rates || {});
        setRound(d.round || 50);
        setUpdatedAt(d.updated_at || null);
      })
      .catch(() => toast.error("Could not load rates"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const save = async () => {
    const payload = { rates: {}, round: Number(round) };
    for (const k of EDITABLE) {
      const v = Number(rates[k]);
      if (!v || v <= 0) { toast.error(`Enter a valid rate for ${MARKETS[k].label}`); return; }
      payload.rates[k] = v;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/fx-rates`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      const d = await res.json();
      setRates(d.rates || {});
      setRound(d.round || 50);
      setUpdatedAt(d.updated_at || null);
      toast.success("Exchange rates updated", { description: "Live across the site immediately." });
    } catch (_) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center text-ink-secondary"><Loader2 className="w-5 h-5 animate-spin mr-2 text-terracotta" /> Loading rates…</div>;

  const preview = (rate) => {
    const usd = 4000;
    const step = Number(round) || 50;
    return Math.round((usd * Number(rate || 0)) / step) * step;
  };

  return (
    <div data-testid="currency-admin" className="max-w-2xl">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="w-9 h-9 bg-moss rounded-sm flex items-center justify-center"><Globe className="w-5 h-5 text-white" /></div>
        <div>
          <h2 className="font-display font-extrabold text-xl tracking-tight leading-none">Currency & FX Rates</h2>
          <p className="font-mono text-[10px] uppercase tracking-widest text-ink-muted mt-1">Managed display rates — USD is the source of truth</p>
        </div>
      </div>
      <p className="text-sm text-ink-secondary mt-3 mb-6">
        Prices are stored in <b>USD</b>. Each market below displays a converted, rounded price using your managed rate
        (1&nbsp;USD → local currency). Customers are always billed in USD at checkout. Changes go live instantly.
      </p>

      <div className="border border-sand-dark rounded-md bg-white divide-y divide-sand-dark">
        <div className="flex items-center justify-between px-4 py-3 bg-sand/40">
          <span className="text-sm font-semibold flex items-center gap-2">{MARKETS.global.flag} {MARKETS.global.label} <span className="font-mono text-xs text-ink-muted">USD</span></span>
          <span className="font-mono text-sm text-ink-muted">Base · 1.00</span>
        </div>
        {EDITABLE.map((k) => (
          <div key={k} className="flex items-center gap-4 px-4 py-4">
            <span className="text-sm font-semibold flex items-center gap-2 w-44 shrink-0">
              {MARKETS[k].flag} {MARKETS[k].label} <span className="font-mono text-xs text-ink-muted">{MARKETS[k].currencyCode}</span>
            </span>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-ink-muted">1 USD =</span>
              <input
                data-testid={`fx-rate-${k}`}
                type="number" step="0.01" min="0"
                value={rates[k] ?? ""}
                onChange={(e) => setRates((r) => ({ ...r, [k]: e.target.value }))}
                className="w-24 h-10 px-2 rounded-sm border border-sand-dark text-ink font-mono text-sm focus:border-moss outline-none"
              />
              <span className="font-mono text-xs text-ink-muted">{MARKETS[k].currency}</span>
            </div>
            <span className="ml-auto font-mono text-xs text-ink-muted">e.g. $4,000 → {MARKETS[k].currency}{preview(rates[k]).toLocaleString()}</span>
          </div>
        ))}
        <div className="flex items-center gap-4 px-4 py-4">
          <span className="text-sm font-semibold w-44 shrink-0">Round to nearest</span>
          <input
            data-testid="fx-round"
            type="number" step="1" min="1"
            value={round}
            onChange={(e) => setRound(e.target.value)}
            className="w-24 h-10 px-2 rounded-sm border border-sand-dark text-ink font-mono text-sm focus:border-moss outline-none"
          />
          <span className="font-mono text-xs text-ink-muted">applies to all converted prices</span>
        </div>
      </div>

      <div className="flex items-center gap-4 mt-5">
        <button data-testid="fx-save" onClick={save} disabled={saving}
          className="flex items-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white px-5 h-11 rounded-sm font-semibold transition-colors disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save rates
        </button>
        {updatedAt && <span className="font-mono text-[11px] text-ink-muted">Last updated {new Date(updatedAt).toLocaleString()}</span>}
      </div>
    </div>
  );
}
